// ChatView - Vollständige Chat-Ansicht

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ArrowLeft, Send, Loader2, MoreVertical, Archive, Flag } from 'lucide-react';
import type { Message } from '../../types/messaging';
import {
  subscribeToMessages,
  sendMessage,
  markAsRead,
  archiveConversation,
} from '../../services/messagingService';
import { Timestamp } from '@firebase/firestore';

interface ChatViewProps {
  conversationId: string;
  userId: string;
  userName: string;
  userPhoto: string | null;
  otherUser: {
    id: string;
    name: string;
    photoURL: string | null;
  };
  onBack: () => void;
  onViewProfile?: (userId: string) => void;
}

export function ChatView({
  conversationId,
  userId,
  userName,
  userPhoto,
  otherUser,
  onBack,
  onViewProfile,
}: ChatViewProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load messages
  useEffect(() => {
    const unsubscribe = subscribeToMessages(conversationId, (msgs) => {
      setMessages(msgs);
      setLoading(false);
    });

    // Mark as read
    markAsRead(conversationId, userId).catch(console.error);

    return () => unsubscribe();
  }, [conversationId, userId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark as read when viewing
  useEffect(() => {
    const interval = setInterval(() => {
      markAsRead(conversationId, userId).catch(console.error);
    }, 5000);

    return () => clearInterval(interval);
  }, [conversationId, userId]);

  const handleSend = useCallback(async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await sendMessage(conversationId, userId, userName, userPhoto, {
        text: newMessage.trim(),
      });
      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  }, [conversationId, userId, userName, userPhoto, newMessage, sending]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleArchive = async () => {
    try {
      await archiveConversation(conversationId);
      onBack();
    } catch (error) {
      console.error('Error archiving conversation:', error);
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = message.createdAt instanceof Timestamp
      ? message.createdAt.toDate()
      : new Date(message.createdAt);

    const dateKey = date.toLocaleDateString('de-DE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
    return groups;
  }, {} as Record<string, Message[]>);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
        <button
          onClick={onBack}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>

        <button
          onClick={() => onViewProfile?.(otherUser.id)}
          className="flex items-center gap-3 flex-1 min-w-0"
        >
          {otherUser.photoURL ? (
            <img
              src={otherUser.photoURL}
              alt={otherUser.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-indigo-600 font-medium">
                {otherUser.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{otherUser.name}</h3>
            <p className="text-xs text-gray-500">Profil ansehen</p>
          </div>
        </button>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-gray-600" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    handleArchive();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
                >
                  <Archive className="w-4 h-4" />
                  <span>Archivieren</span>
                </button>
                <button
                  onClick={() => setShowMenu(false)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left text-red-600 hover:bg-red-50"
                >
                  <Flag className="w-4 h-4" />
                  <span>Melden</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
              {otherUser.photoURL ? (
                <img
                  src={otherUser.photoURL}
                  alt={otherUser.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <span className="text-indigo-600 font-medium text-2xl">
                  {otherUser.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <p className="text-gray-700 font-medium mb-1">{otherUser.name}</p>
            <p className="text-sm text-gray-500">
              Schreibe deine erste Nachricht
            </p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date}>
              {/* Date separator */}
              <div className="flex items-center justify-center mb-4">
                <span className="px-3 py-1 bg-white text-gray-500 text-xs rounded-full shadow-sm">
                  {date}
                </span>
              </div>

              {/* Messages for this date */}
              <div className="space-y-2">
                {msgs.map((message, index) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isOwn={message.senderId === userId}
                    showAvatar={
                      index === 0 || msgs[index - 1].senderId !== message.senderId
                    }
                    otherUserPhoto={otherUser.photoURL}
                    otherUserName={otherUser.name}
                  />
                ))}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nachricht schreiben..."
              rows={1}
              className="w-full px-4 py-3 bg-gray-100 border-0 rounded-2xl resize-none focus:ring-2 focus:ring-indigo-500 focus:bg-white max-h-32"
              style={{
                minHeight: '48px',
                height: 'auto',
              }}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="p-3 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  showAvatar: boolean;
  otherUserPhoto: string | null;
  otherUserName: string;
}

function MessageBubble({
  message,
  isOwn,
  showAvatar,
  otherUserPhoto,
  otherUserName,
}: MessageBubbleProps) {
  const time = message.createdAt instanceof Timestamp
    ? message.createdAt.toDate()
    : new Date(message.createdAt);

  return (
    <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {!isOwn && (
        <div className="w-8 flex-shrink-0">
          {showAvatar && (
            otherUserPhoto ? (
              <img
                src={otherUserPhoto}
                alt={otherUserName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-600 text-sm font-medium">
                  {otherUserName.charAt(0).toUpperCase()}
                </span>
              </div>
            )
          )}
        </div>
      )}

      {/* Message */}
      <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn
              ? 'bg-indigo-500 text-white rounded-br-md'
              : 'bg-white text-gray-900 rounded-bl-md shadow-sm'
          }`}
        >
          <p className="whitespace-pre-wrap break-words">{message.text}</p>
        </div>
        <div
          className={`flex items-center gap-1 mt-1 ${
            isOwn ? 'justify-end' : 'justify-start'
          }`}
        >
          <span className="text-xs text-gray-400">
            {time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </span>
          {isOwn && message.status === 'read' && (
            <span className="text-xs text-indigo-400">Gelesen</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatView;
